import { useEffect, useState } from "react";

const carriesFiles = (event: DragEvent) => !!event.dataTransfer && Array.from(event.dataTransfer.types).includes("Files");

/**
 * True while a file from outside the browser is dragged over the window. Lets empty language boxes
 * invite the drop as soon as the file enters the app, not only when the pointer finds the box.
 * dragenter/dragleave fire for every element crossed, hence the depth count.
 */
export const useWindowFileDrag = () => {
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  useEffect(() => {
    let depth = 0;
    const onEnter = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      depth += 1;
      setIsDraggingFile(true);
    };
    const onLeave = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setIsDraggingFile(false);
    };
    const onEnd = () => {
      depth = 0;
      setIsDraggingFile(false);
    };
    // Inviting file drags means files get dropped beside the target, and the browser's default is to
    // open the dropped file in place of the editor. Drop zones handle the event first (and prevent
    // its default); anything still unhandled when it reaches the window is swallowed.
    const onOver = (event: DragEvent) => {
      if (!carriesFiles(event) || event.defaultPrevented) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
    };
    const onDrop = (event: DragEvent) => {
      if (carriesFiles(event) && !event.defaultPrevented) event.preventDefault();
    };

    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onEnd, true);
    window.addEventListener("drop", onDrop);
    window.addEventListener("dragend", onEnd);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onEnd, true);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("dragend", onEnd);
    };
  }, []);

  return isDraggingFile;
};
